import React from "react";
import { AlertTriangle, ArrowUpRight, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { paymentStateFor, type StripeConnectedProject } from "@/utils/stripeConnectedProjects";

interface Props {
  projects: StripeConnectedProject[];
  onOpenProject: (projectId: string) => void;
}

const StripeConnectedProjects: React.FC<Props> = ({ projects, onOpenProject }) => (
  <section className="rounded-xl border border-gray-200 p-3 dark:border-white/10" aria-labelledby="stripe-connected-projects">
    <h3 id="stripe-connected-projects" className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {projects.length === 1 ? "Connected project" : `Connected projects (${projects.length})`}
    </h3>

    {projects.length === 0 ? (
      <p className="mt-2 text-xs text-muted-foreground">
        No projects have Stripe connected yet. Connect one and it will show here.
      </p>
    ) : (
      <ul className="mt-2 space-y-1.5">
        {projects.map((project) => {
          const payment = paymentStateFor(project);
          return (
            <li
              key={project.projectId}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-2 dark:border-white/10"
            >
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{project.projectName}</span>
              {project.modes.map((mode) => (
                <Badge
                  key={mode}
                  variant="secondary"
                  title={project.envMode === mode ? "These keys are in the project's environment" : undefined}
                  className={`px-1.5 py-0 text-[10px] font-medium ${
                    mode === "live"
                      ? "bg-ink-blue-900/10 text-ink-blue-900 hover:bg-ink-blue-900/10 dark:bg-white/10 dark:text-white"
                      : "bg-gray-100 text-muted-foreground hover:bg-gray-100 dark:bg-white/10 dark:text-gray-400"
                  }`}
                >
                  {mode === "live" ? "Live" : "Test"}
                  {project.envMode === mode && project.modes.length > 1 ? " · in use" : ""}
                </Badge>
              ))}
              <Badge
                variant="secondary"
                className={`gap-1 px-1.5 py-0 text-[10px] font-medium ${
                  payment.ready
                    ? "bg-emerald-50 text-emerald-700 hover:bg-success-bg dark:text-emerald-400"
                    : "bg-amber-50 text-amber-700 hover:bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400"
                }`}
              >
                {payment.ready ? <Check className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
                {payment.label}
              </Badge>
              <button
                type="button"
                onClick={() => onOpenProject(project.projectId)}
                aria-label={`Open ${project.projectName}`}
                className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] text-foreground hover:bg-gray-100 dark:hover:bg-white/10"
              >
                Open
                <ArrowUpRight className="h-3 w-3" />
              </button>
            </li>
          );
        })}
      </ul>
    )}
    <p className="mt-2 text-[11px] text-muted-foreground">Disconnect Stripe from inside a project, where its keys are removed from that project's environment.</p>
  </section>
);

export default StripeConnectedProjects;
